import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { fetchShippingOptionsWithLocation, fetchShippingConstants } from 'base/actions/asynchronous'
import { hasFreeShipping, intlUserHasFreeShippingCoupon, isFirstOrder } from 'base/lib/user'
import { getCartTotal } from 'base/reducers/cart'
import { getLocalization } from 'base/lib/localization'
import { getFreeShippingThreshold } from 'base/lib/shipping_copy'

const ShippingProgressBar = props => {
  const {
    address,
    shippingOptions,
    cartTotal,
    user,
    fetchShippingOptions,
    getShippingConstants,
    intlFirstOrderFreeShippingEnabled,
  } = props

  // fetch all the data
  useEffect(() => {
    const { city, country_code, postal_code } = getLocalization() || {}
    fetchShippingOptions({
      city,
      country: country_code,
      postal_code,
      region_code: '',
    })
    getShippingConstants()
  }, [fetchShippingOptions, getShippingConstants])

  const localeCountry = getLocalization().country_code
  const country = address?.country || localeCountry

  // check if the user has free shipping before showing progress bar
  const userHasFreeShipping =
    user && (hasFreeShipping(user, country) || intlUserHasFreeShippingCoupon(user, country))
  const intlUserHasFirstOrderFreeShipping =
    user && intlFirstOrderFreeShippingEnabled && isFirstOrder(user) && address?.country !== 'US'

  if (userHasFreeShipping || intlUserHasFirstOrderFreeShipping) {
    return (
      <div className="shipping-progress-bar__text shipping-progress-bar__text--no-bar">
        Free standard shipping on your next order
      </div>
    )
  }

  const shippingThreshold = getFreeShippingThreshold({
    localeCountry,
    shippingOptions,
    addressCountry: country,
  })

  if (!shippingThreshold || !cartTotal) {
    return null
  }

  const difference = shippingThreshold - cartTotal
  const hasAchievedFreeShipping = difference <= 0
  const value = hasAchievedFreeShipping ? shippingThreshold : cartTotal
  const percent = (value / shippingThreshold) * 100

  return (
    <div className="shipping-progress-bar">
      <div className="shipping-progress-bar__text">
        {hasAchievedFreeShipping
          ? 'Congrats! You get free shipping'
          : `${getLocalization().symbol}${difference} away from free shipping`}
      </div>
      <div className="progress-bar">
        <div
          className={classNames('progress-bar--filled', 'progress-bar--green')}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

ShippingProgressBar.propTypes = {
  address: PropTypes.object,
  cartTotal: PropTypes.number,
  shippingOptions: PropTypes.array,
  user: PropTypes.object,
  fetchShippingOptions: PropTypes.func,
  getShippingConstants: PropTypes.func,
  intlFirstOrderFreeShippingEnabled: PropTypes.bool,
}

function mapStateToProps(state) {
  return {
    address: state.cart.shipping_address,
    cartTotal: getCartTotal(state.cart),
    shippingOptions: state.checkout.shippingOptions,
    user: state.session.user,
    intlFirstOrderFreeShippingEnabled: state.appConfig.intl_first_order_free_shipping_enabled,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    fetchShippingOptions: location => dispatch(fetchShippingOptionsWithLocation(location)),
    getShippingConstants: () => dispatch(fetchShippingConstants()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShippingProgressBar)
